import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styled from "styled-components";

const API = "https://admin.shopaholics.ge/api";

/* ---------- adapters / utils ---------- */
const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.products)) return d.products;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d)) return d;
  return [];
};

const normalizeProducts = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((x) => {
      const product_id = x.product_id ?? x.id ?? x.ID ?? x.pid ?? null;
      const name = x.name ?? x.title ?? x.product_name ?? x.Name ?? "";
      const brand = x.brand ?? x.Brand ?? x.manufacturer ?? "";
      const img = x.image_url ?? x.image ?? x.main_image ?? x.cover_image ?? x.thumbnail ?? "";
      const category = x.category ?? x.product_category ?? x.type ?? "";
      return { ...x, product_id, name, brand, _normalized_image: img, category };
    })
    .filter((p) => p.product_id != null);
};

const pidOf = (x) => x.product_id ?? x.id;

/* ---------- Styled ---------- */
const Page = styled.div`
  max-width: 1200px;
  margin: auto 60px;
  padding: 24px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;
const Panel = styled.div`
  background: #fff; border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.06);
  border: 1px solid #f0f0f0; overflow: hidden;
`;
const PanelHead = styled.div`
  position: sticky; top: 0; background: #fff; padding: 16px 18px; border-bottom: 1px solid #f3f3f3;
  h3 { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: 0.2px; }
  small { color: #888; }
`;
const PanelBody = styled.div` padding: 16px 18px; `;
const Toolbar = styled.div`
  display: flex; gap: 10px; margin: 12px 0 10px; flex-wrap: wrap; align-items: center;
  input { flex: 1; min-width: 220px; border: 1px solid #e6e6e6; background: #fafafa; border-radius: 12px; padding: 10px 12px; outline: none; font-size: 14px; }
  input:focus { background: #fff; border-color: #bdbdbd; }
  select { border: 1px solid #e6e6e6; background: #fafafa; border-radius: 12px; padding: 10px 12px; font-size: 14px; }
  button { border: 0; border-radius: 12px; padding: 10px 14px; font-weight: 700; cursor: pointer; }
  .ghost { background: #f4f4f4; } .ghost:hover { background: #eaeaea; }
  .primary { background: #111; color: #fff; } .primary:disabled { opacity: .5; cursor: not-allowed; }
  .danger { background: #ffecec; color: #c12020; }
  .danger:hover { background: #ffdede; }
`;
const List = styled.div` border: 1px dashed #eee; border-radius: 14px; max-height: 480px; overflow: auto; `;
const Row = styled.label`
  display: grid; grid-template-columns: 28px 1fr auto; align-items: center; gap: 10px; padding: 10px 12px; border-bottom: 1px solid #f7f7f7;
  &:hover { background: #fafafa; }
  .thumb { grid-column: 2 / 3; display: flex; align-items: center; gap: 12px; }
  img { width: 44px; height: 44px; border-radius: 10px; object-fit: cover; background: #f4f4f4; border: 1px solid #eee; }
  .meta { display: flex; flex-direction: column; line-height: 1.2; }
  .title { font-weight: 700; font-size: 14px; color: #111; }
  .sub { font-size: 12px; color: #777; }
  .danger { grid-column: 3 / 4; justify-self: end; border: 0; background: #ffecec; color: #d62f2f; padding: 8px 10px; border-radius: 10px; font-weight: 700; cursor: pointer; }
`;
const FooterActions = styled.div`
  display: flex; justify-content: space-between; margin-top: 12px; align-items: center; gap: 12px; flex-wrap: wrap;
  .count { color: #666; font-size: 13px; }
  .actions { display: flex; gap: 10px; }
  .actions button { border: 0; border-radius: 12px; padding: 10px 14px; font-weight: 700; cursor: pointer; }
  .actions .primary { background: #111; color: #fff; }
  .actions .ghost { background: #f4f4f4; }
`;
const Empty = styled.div` padding: 18px; text-align: center; color: #777; `;
const Badge = styled.span`
  font-size: 12px; font-weight: 700; padding: 6px 10px; border-radius: 999px;
  background: ${p => p.$active ? "#e8f8ee" : "#fff2e8"};
  color: ${p => p.$active ? "#107e3e" : "#a64b00"};
  border: 1px solid ${p => p.$active ? "#bfead1" : "#ffe0c7"};
`;

/* ---------- Component ---------- */
export default function CollectionAssign({ collectionId }) {
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(collectionId ?? "");
  const [allProducts, setAllProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [members, setMembers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [deletingCollection, setDeletingCollection] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true); // ✅ filter toggle

  /* --- Load collections list --- */
  const fetchCollections = async () => {
    setLoadingCollections(true);
    try {
      const params = activeOnly ? { active: 1 } : {};
      const res = await axios.get(`${API}/collections/list.php`, { params });
      const arr = toArray(res);
      setCollections(arr);
      // auto-select
      if (!collectionId) {
        if (arr.length) setSelectedCollectionId(prev => prev && arr.some(x => String(x.id)===String(prev)) ? prev : arr[0].id);
        else setSelectedCollectionId("");
      }
    } catch {
      setCollections([]);
    } finally {
      setLoadingCollections(false);
    }
  };

  useEffect(() => {
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, activeOnly]);

  /* --- Load all products --- */
  useEffect(() => {
    (async () => {
      setLoadingProducts(true);
      try {
        const res = await axios.get(`${API}/products/list.php`);
        const raw = toArray(res);
        setAllProducts(normalizeProducts(raw));
      } catch {
        setAllProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  /* --- Load members of selected collection --- */
  useEffect(() => {
    if (!selectedCollectionId) {
      setMembers([]);
      setSelectedProductIds([]);
      return;
    }
    setLoadingMembers(true);
    axios
      .get(`${API}/collections/items.php`, { params: { collection_id: selectedCollectionId } })
      .then((res) => setMembers(normalizeProducts(toArray(res))))
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
    setSelectedProductIds([]);
  }, [selectedCollectionId]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const src = Array.isArray(allProducts) ? allProducts : [];
    if (!q) return src;
    return src.filter(
      (p) => (p.name || "").toLowerCase().includes(q) || String(pidOf(p)) === q
    );
  }, [allProducts, query]);

  const toggleSelect = (id) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllFiltered = () => {
    const ids = (Array.isArray(filtered) ? filtered : []).map((p) => pidOf(p));
    setSelectedProductIds(ids);
  };

  const clearSelection = () => setSelectedProductIds([]);

  const addSelected = async () => {
    if (!selectedCollectionId || selectedProductIds.length === 0) return;
    try {
      await axios.post(`${API}/collections/add_items.php`, {
        collection_id: Number(selectedCollectionId),
        product_ids: selectedProductIds,
      });
      setSelectedProductIds([]);
      const res = await axios.get(`${API}/collections/items.php`, {
        params: { collection_id: selectedCollectionId },
      });
      setMembers(normalizeProducts(toArray(res)));
    } catch { /* ignore */ }
  };

  const removeOne = async (pid) => {
    try {
      await axios.post(`${API}/collections/remove_item.php`, {
        collection_id: Number(selectedCollectionId),
        product_id: pid,
      });
      setMembers((prev) =>
        Array.isArray(prev) ? prev.filter((m) => pidOf(m) !== pid) : []
      );
    } catch { /* ignore */ }
  };

  const deleteCollection = async () => {
    if (!selectedCollectionId) return;
    const col = collections.find((c) => String(c.id) === String(selectedCollectionId));
    const name = col ? (col.name_ka || col.name_en || col.slug || `#${col.id}`) : `#${selectedCollectionId}`;
    if (!window.confirm(`წაშლა? კოლექცია "${name}" და მისი ყველა item წაიშლება.`)) return;

    setDeletingCollection(true);
    try {
      await axios.post(`${API}/collections/delete.php`, { id: Number(selectedCollectionId) });
      await fetchCollections();
      setMembers([]);
      setSelectedProductIds([]);
    } catch { /* ignore */ }
    finally { setDeletingCollection(false); }
  };

  // ✅ Toggle active/passive for selected collection
  const toggleActive = async () => {
    const col = collections.find((c) => String(c.id) === String(selectedCollectionId));
    if (!col) return;
    const nextVal = String(col.is_active ?? col.active ?? 1) === "1" ? 0 : 1;

    // Send as FormData (widely compatible)
    const fd = new FormData();
    fd.append("id", selectedCollectionId);
    fd.append("is_active", String(nextVal));

    try {
      await axios.post(`${API}/collections/update.php`, fd);
      await fetchCollections();
      // თუ activeOnly=True და დეაქტივაცია მოხდა — არჩეული შეიძლება გაქრეს სიიდან.
      // fetchCollections უკვე გადაგვირჩევს პირველ ხელმისაწვდომს.
    } catch { /* ignore */ }
  };

  const thumbOf = (item) => item._normalized_image || "";

  const selectedCollection = Array.isArray(collections)
    ? collections.find((c) => String(c.id) === String(selectedCollectionId))
    : null;

  const selectedIsActive = selectedCollection
    ? String(selectedCollection.is_active ?? selectedCollection.active ?? 1) === "1"
    : false;

  return (
    <Page>
      <Grid>
        <Panel>
          <PanelHead>
            <h3>
              სუნამოების სია{" "}
              <small>
                {loadingProducts
                  ? "იტვირთება…"
                  : `(${Array.isArray(filtered) ? filtered.length : 0})`}
              </small>
            </h3>
            <Toolbar>
              {/* კოლექციის selector */}
              <select
                value={selectedCollectionId || ""}
                onChange={(e) => setSelectedCollectionId(e.target.value)}
                disabled={!!collectionId || loadingCollections}
                title={collectionId ? "ფიქსირებული კოლექცია prop-ით გადმოსულია" : ""}
              >
                <option value="" disabled>
                  {loadingCollections ? "კოლექციები იტვირთება…" : "აირჩიე კოლექცია"}
                </option>
                {(Array.isArray(collections) ? collections : []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.name_ka || c.name_en || c.slug)} #{c.id}
                    {String(c.is_active ?? 1) !== "1" ? " (არააქტიური)" : ""}
                  </option>
                ))}
              </select>

              {/* მხოლოდ აქტიურები / ყველას ნახვა */}
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                  title="მხოლოდ აქტიურების გამოჩენა"
                />
                <span style={{ fontSize: 13, color: "#333" }}>მხოლოდ აქტიურები</span>
              </label>

              {/* არჩეული კოლექციის სტატუსი */}
              {selectedCollection && (
                <Badge $active={selectedIsActive}>
                  {selectedIsActive ? "აქტიური" : "არააქტიური"}
                </Badge>
              )}

              <input
                placeholder="ძიება სახელით ან ID-ით…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />

              <button className="ghost" onClick={selectAllFiltered}>Select all (filtered)</button>
              <button className="ghost" onClick={clearSelection}>Clear</button>
              <button
                className="primary"
                onClick={addSelected}
                disabled={!selectedCollectionId || selectedProductIds.length === 0}
              >
                დაამატე არჩეული
              </button>

              {/* Toggle active/passive */}
              <button
                className="ghost"
                onClick={toggleActive}
                disabled={!selectedCollectionId}
                title="გააქტიურება/დეაქტივაცია"
              >
                {selectedIsActive ? "დეაქტივაცია" : "გააქტიურება"}
              </button>

              {/* Danger: delete selected collection */}
              <button
                className="danger"
                onClick={deleteCollection}
                disabled={!selectedCollectionId || deletingCollection}
                title="კოლექციის წაშლა და მის წევრთა გასუფთავება"
              >
                {deletingCollection ? "შეჩერება…" : "წაშლა"}
              </button>
            </Toolbar>
          </PanelHead>

          <PanelBody>
            {loadingProducts ? (
              <Empty>იტვირთება…</Empty>
            ) : Array.isArray(filtered) && filtered.length ? (
              <>
                <List>
                  {filtered.map((p) => (
                    <Row key={pidOf(p)}>
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(pidOf(p))}
                        onChange={() => toggleSelect(pidOf(p))}
                      />
                      <div className="thumb">
                        <img
                          src={thumbOf(p)}
                          alt=""
                          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                        />
                        <div className="meta">
                          <div className="title">
                            {p.name} #{pidOf(p)}
                          </div>
                          <div className="sub">{p.brand || p.category || "—"}</div>
                        </div>
                      </div>
                      <div />
                    </Row>
                  ))}
                </List>

                <FooterActions>
                  <div className="count">
                    არჩეული კოლექცია:{" "}
                    <b>
                      {selectedCollection
                        ? selectedCollection.name_ka ||
                          selectedCollection.name_en ||
                          selectedCollection.slug
                        : "—"}
                    </b>
                  </div>
                  <div className="actions">
                    <div className="count">
                      მონიშნულია: <b>{selectedProductIds.length}</b>
                    </div>
                    <button className="ghost" onClick={clearSelection}>Clear</button>
                    <button
                      className="primary"
                      onClick={addSelected}
                      disabled={!selectedCollectionId || selectedProductIds.length === 0}
                    >
                      დაამატე
                    </button>
                  </div>
                </FooterActions>
              </>
            ) : (
              <Empty>სია ცარიელია.</Empty>
            )}
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead>
            <h3>
              კოლექციის წევრები{" "}
              <small>
                {loadingMembers
                  ? "იტვირთება…"
                  : `(${Array.isArray(members) ? members.length : 0})`}
              </small>
            </h3>
          </PanelHead>
          <PanelBody>
            {selectedCollectionId ? (
              Array.isArray(members) && members.length ? (
                <List>
                  {members.map((m) => (
                    <Row as="div" key={pidOf(m)}>
                      <div />
                      <div className="thumb">
                        <img
                          src={thumbOf(m)}
                          alt=""
                          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                        />
                        <div className="meta">
                          <div className="title">
                            {m.name} #{pidOf(m)}
                          </div>
                          <div className="sub">{m.brand || m.category || "—"}</div>
                        </div>
                      </div>
                      <button className="danger" onClick={() => removeOne(pidOf(m))}>
                        Remove
                      </button>
                    </Row>
                  ))}
                </List>
              ) : (
                <Empty>{loadingMembers ? "იტვირთება…" : "ამ კოლექციაში ჯერ არაფერი დაემატა."}</Empty>
              )
            ) : (
              <Empty>ჯერ აირჩიე კოლექცია მარცხენა პანელიდან.</Empty>
            )}
          </PanelBody>
        </Panel>
      </Grid>
    </Page>
  );
}
